import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * The Constructivist button: condensed uppercase label, square corners, and a
 * hard offset shadow instead of a soft blur. The shadow is what carries the
 * press interaction — on hover the button lifts away from it, on press it drops
 * back onto it — so the motion stays consistent with the visual language.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary text-on-primary border border-primary shadow-hard',
    'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:bg-primary-hover hover:shadow-[8px_8px_0_var(--text-heading)]',
    'active:translate-x-[3px] active:translate-y-[3px] active:shadow-none',
  ),
  secondary: cn(
    'bg-surface-inverse text-inverse border border-surface-inverse shadow-hard',
    'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[8px_8px_0_var(--accent)]',
    'active:translate-x-[3px] active:translate-y-[3px] active:shadow-none',
  ),
  outline: cn(
    'bg-transparent text-heading border-2 border-line-strong',
    'hover:bg-surface-muted hover:border-heading',
    'active:translate-y-[1px]',
  ),
  ghost: cn(
    'bg-transparent text-body border border-transparent',
    'hover:bg-surface-muted hover:text-heading',
    'active:bg-surface-alt',
  ),
  danger: cn(
    // `text-on-primary` rather than the Tailwind palette colour `text-white`:
    // the pairing flips with the theme, so the label keeps its contrast when
    // --danger lightens to #FF9B92 in dark mode (8.50:1) and stays 6.54:1 on
    // the light-mode #B3261E.
    'bg-danger text-on-primary border border-danger shadow-hard',
    // The lift shadow takes `--danger` itself, never `--danger-surface`. A
    // surface tint is a fill, and on the dark canvas (#181B22) the dark-red
    // #3D1F1C offset measured 1.16:1 — a hover that vanished exactly where the
    // shadow is the whole interaction. Measured now: 5.55:1 on light, 8.50:1 on
    // dark.
    'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:brightness-110 hover:shadow-[8px_8px_0_var(--danger)]',
    'active:translate-x-[3px] active:translate-y-[3px] active:shadow-none',
  ),
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-2xs gap-1.5',
  md: 'h-10 px-5 text-xs gap-2',
  lg: 'h-12 px-7 text-sm gap-2.5',
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-[18px]',
};

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the leading slot for a spinner and blocks interaction. */
  loading?: boolean;
  /** Replaces the label while `loading` is true, so the state is announced. */
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Stretches to the container width. */
  block?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  leadingIcon,
  trailingIcon,
  block = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      {...rest}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        // The hit target is guaranteed at 44px even at the `sm` size, where the
        // visual height is only 32px.
        'dhd-target relative inline-flex select-none items-center justify-center',
        'rounded-xs font-display font-semibold uppercase tracking-label',
        'transition-[transform,background-color,color,box-shadow,border-color,filter] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        // Loading is disabled for semantics, but it is NOT dimmed: a loading
        // button is the thing the user is waiting on, and pouring `opacity-45`
        // over it made "working" and "unavailable" look identical but for the
        // spinner. Only a genuinely disabled button fades.
        'disabled:pointer-events-none',
        !loading && 'disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
    >
      {loading ? (
        <Spinner size={size === 'sm' ? 'sm' : 'md'} label={loadingLabel ?? undefined} />
      ) : leadingIcon ? (
        <span className={cn('shrink-0 [&>svg]:size-full', ICON_SIZES[size])} aria-hidden="true">
          {leadingIcon}
        </span>
      ) : null}

      {children}

      {trailingIcon && !loading ? (
        <span className={cn('shrink-0 [&>svg]:size-full', ICON_SIZES[size])} aria-hidden="true">
          {trailingIcon}
        </span>
      ) : null}
    </button>
  );
}
